import { Input, Textarea, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { CharacterBackground } from '@creanizer/types'

interface BackgroundEditorProps {
  background: CharacterBackground
  onChange: (background: CharacterBackground) => void
}

export function BackgroundEditor({
  background,
  onChange
}: BackgroundEditorProps) {
  const [newEvent, setNewEvent] = useState('')
  const [newRelationship, setNewRelationship] = useState('')
  const [newSecret, setNewSecret] = useState('')
  const [newAbility, setNewAbility] = useState('')

  const handleArrayAdd = (field: 'significantEvents' | 'relationships' | 'secrets' | 'abilities', value: string) => {
    if (value) {
      onChange({
        ...background,
        [field]: [...(background[field] || []), value]
      })
      if (field === 'significantEvents') setNewEvent('')
      if (field === 'relationships') setNewRelationship('')
      if (field === 'secrets') setNewSecret('')
      if (field === 'abilities') setNewAbility('')
    }
  }

  const handleArrayRemove = (field: 'significantEvents' | 'relationships' | 'secrets' | 'abilities', index: number) => {
    onChange({
      ...background,
      [field]: (background[field] || []).filter((_, i) => i !== index)
    })
  }

  const handleArrayUpdate = (field: 'significantEvents' | 'relationships' | 'secrets' | 'abilities', index: number, value: string) => {
    const updated = [...(background[field] || [])]
    updated[index] = value
    onChange({
      ...background,
      [field]: updated
    })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="species">種族・民族</Label>
          <Input
            id="species"
            value={background.species || ''}
            onChange={(e) => onChange({ ...background, species: e.target.value })}
            placeholder="例: 人間、エルフ、日本人"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="language">言語・方言</Label>
          <Input
            id="language"
            value={background.language || ''}
            onChange={(e) => onChange({ ...background, language: e.target.value })}
            placeholder="例: 標準語、関西弁、古語"
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="birthplace">出身地</Label>
          <Input
            id="birthplace"
            value={background.birthplace}
            onChange={(e) => onChange({ ...background, birthplace: e.target.value })}
            placeholder="出身地"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="occupation">職業</Label>
          <Input
            id="occupation"
            value={background.occupation}
            onChange={(e) => onChange({ ...background, occupation: e.target.value })}
            placeholder="職業"
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="family">家族構成</Label>
        <Textarea
          id="family"
          value={background.family}
          onChange={(e) => onChange({ ...background, family: e.target.value })}
          placeholder="家族構成と関係性"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="education">教育・訓練</Label>
        <Textarea
          id="education"
          value={background.education}
          onChange={(e) => onChange({ ...background, education: e.target.value })}
          placeholder="教育・訓練の背景"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">重要な出来事</Label>
        <div className="space-y-2 mt-2">
          {background.significantEvents.map((event, index) => {
            // オブジェクトの場合は文字列に変換
            const eventString = typeof event === 'string' ? event : JSON.stringify(event)
            return (
              <div key={index} className="flex gap-2">
                <Input
                  value={eventString}
                  onChange={(e) => handleArrayUpdate('significantEvents', index, e.target.value)}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleArrayRemove('significantEvents', index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
          <div className="flex gap-2">
            <Input
              value={newEvent}
              onChange={(e) => setNewEvent(e.target.value)}
              placeholder="例: 10歳の時に両親を失った"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('significantEvents', newEvent)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('significantEvents', newEvent)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">人間関係</Label>
        <div className="space-y-2 mt-2">
          {background.relationships.map((rel, index) => {
            // オブジェクトの場合は文字列に変換
            const relString = typeof rel === 'string' ? rel : JSON.stringify(rel)
            return (
              <div key={index} className="flex gap-2">
                <Input
                  value={relString}
                  onChange={(e) => handleArrayUpdate('relationships', index, e.target.value)}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleArrayRemove('relationships', index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
          <div className="flex gap-2">
            <Input
              value={newRelationship}
              onChange={(e) => setNewRelationship(e.target.value)}
              placeholder="例: 師匠のアルベルトを尊敬している"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('relationships', newRelationship)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('relationships', newRelationship)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">秘密</Label>
        <div className="space-y-2 mt-2">
          {(background.secrets || []).map((secret, index) => {
            // オブジェクトの場合は文字列に変換
            const secretString = typeof secret === 'string' ? secret : JSON.stringify(secret)
            return (
              <div key={index} className="flex gap-2">
                <Input
                  value={secretString}
                  onChange={(e) => handleArrayUpdate('secrets', index, e.target.value)}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleArrayRemove('secrets', index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
          <div className="flex gap-2">
            <Input
              value={newSecret}
              onChange={(e) => setNewSecret(e.target.value)}
              placeholder="例: 実は王家の血を引いている"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('secrets', newSecret)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('secrets', newSecret)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">能力・スキル</Label>
        <div className="space-y-2 mt-2">
          {(background.abilities || []).map((ability, index) => {
            const abilityString = typeof ability === 'string' ? ability : JSON.stringify(ability)
            return (
              <div key={index} className="flex gap-2">
                <Input
                  value={abilityString}
                  onChange={(e) => handleArrayUpdate('abilities', index, e.target.value)}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleArrayRemove('abilities', index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
          <div className="flex gap-2">
            <Input
              value={newAbility}
              onChange={(e) => setNewAbility(e.target.value)}
              placeholder="例: 剣術、料理、魔法、交渉術"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('abilities', newAbility)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('abilities', newAbility)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="backstoryNarrative">バックストーリー（物語形式）</Label>
        <Textarea
          id="backstoryNarrative"
          value={background.backstoryNarrative || ''}
          onChange={(e) => onChange({ ...background, backstoryNarrative: e.target.value })}
          placeholder="キャラクターの生い立ちや経験を物語形式で記述..."
          rows={6}
          className="mt-1"
        />
      </div>
    </div>
  )
}